import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { claimWelcomeEmailSend } from "@/lib/welcome-email.functions";
import { sendTransactionalEmail } from "@/lib/email/send";
import { track } from "@/lib/analytics";

/**
 * Skickar "Login Completed" högst en gång per webbläsarsession, så vi kan
 * mäta hur många av besöken på inloggningssidan som faktiskt slutar i en
 * inloggad session (dvs. om inloggningslänkarna är ett hinder).
 */
function trackLoginOnce() {
  if (typeof window === "undefined") return;
  try {
    const key = "ap.login-tracked";
    if (window.sessionStorage?.getItem(key)) return;
    window.sessionStorage?.setItem(key, "1");
  } catch {
    /* sessionStorage kan vara blockerad – mät ändå */
  }
  track("Login Completed");
}

async function maybeSendWelcome() {
  try {
    const res = await claimWelcomeEmailSend();
    if (!res?.shouldSend || !res.email) return;
    await sendTransactionalEmail({
      templateName: "welcome-signup",
      recipientEmail: res.email,
      idempotencyKey: `welcome-${res.email}`,
      templateData: res.name ? { name: res.name } : {},
    });
  } catch (e) {
    console.warn("welcome email skipped", e);
  }
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (event === "SIGNED_IN" && s?.user?.email_confirmed_at) {
        void maybeSendWelcome();
      }
      if (event === "SIGNED_IN" && s?.user) trackLoginOnce();
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
      if (data.session?.user) trackLoginOnce();
    });

    return () => subscription.unsubscribe();
  }, []);

  return { session, user, loading };
}
